"use client";
import React, { useState, useEffect, useRef } from 'react';

const CommunityStats = ({ graphQLData }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({
    members: 0,
    projects: 0,
    challenges: 0,
    articles: 0
  });
  const sectionRef = useRef(null);

  // Calculate stats from graphQLData
  useEffect(() => {
    if (graphQLData) {
      const stats = {
        members: 8500, // This could come from API or be calculated
        projects: graphQLData?.designReviews?.nodes?.length || 0,
        challenges: graphQLData?.challenges?.nodes?.length || 0,
        articles: graphQLData?.posts?.nodes?.length || 0
      };
      setCounts(stats);
    }
  }, [graphQLData]);

  // Intersection Observer for animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const [animatedCounts, setAnimatedCounts] = useState({
    members: 0,
    projects: 0,
    challenges: 0,
    articles: 0
  });

  useEffect(() => {
    if (isVisible) {
      const duration = 2000;
      const steps = 60;
      const increment = duration / steps;
      let step = 0;

      const interval = setInterval(() => {
        step++;
        const progress = step / steps;
        
        setAnimatedCounts({
          members: Math.floor(counts.members * progress),
          projects: Math.floor(counts.projects * progress),
          challenges: Math.floor(counts.challenges * progress),
          articles: Math.floor(counts.articles * progress)
        });

        if (step >= steps) {
          clearInterval(interval);
          setAnimatedCounts(counts);
        }
      }, increment);

      return () => clearInterval(interval);
    }
  }, [isVisible, counts]);

  const stats = [
    {
      icon: 'bi-people-fill',
      value: animatedCounts.members.toLocaleString('ar-EG'),
      label: 'عضو نشط',
      color: '#FFD52E'
    },
    {
      icon: 'bi-folder-fill',
      value: animatedCounts.projects.toLocaleString('ar-EG'),
      label: 'مشروع',
      color: '#4CAF50'
    },
    {
      icon: 'bi-trophy-fill',
      value: animatedCounts.challenges.toLocaleString('ar-EG'),
      label: 'تحدي',
      color: '#FF8C00'
    },
    {
      icon: 'bi-file-text-fill',
      value: animatedCounts.articles.toLocaleString('ar-EG'),
      label: 'مقال',
      color: '#2196F3'
    }
  ];

  return (
    <div 
      id="community-stats"
      ref={sectionRef}
      className="my-5 py-4 mx-auto col-12 col-md-12 col-xxl-8 fade-in-up"
    >
      <div className="row g-3 g-md-4">
        {stats.map((stat, index) => (
          <div key={index} className="col-6 col-md-3">
            <div 
              className="card bg-dark border-0 shadow-sm h-100 text-center p-3 p-md-4"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                borderRadius: '16px',
                transition: 'all 0.3s ease',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = `0 8px 25px ${stat.color}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
              }}
            >
              <div className="mb-2">
                <i 
                  className={`bi ${stat.icon}`}
                  style={{
                    fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                    color: stat.color
                  }}
                />
              </div>
              <h3 
                className="fw-bold mb-1"
                style={{
                  fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
                  color: stat.color
                }}
              >
                {stat.value}
                {index === 0 && '+'}
              </h3>
              <p 
                className="text-muted mb-0"
                style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)' }}
              >
                {stat.label}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommunityStats;
